import { useState, useEffect, useMemo, useCallback } from 'react';
import { Chat, ChatStore, ChatCategory } from '../types/chat';

const STORAGE_KEY = 'chat_store';

let globalStore: ChatStore = {
  chats: {},
  activeChat: null,
  settings: {
    notifications: false,
    soundEnabled: false,
    language: 'en',
    theme: 'light'
  },
  searchQuery: ''
};

// Load initial data
const stored = localStorage.getItem(STORAGE_KEY);
if (stored) {
  globalStore = JSON.parse(stored);
}

type Subscriber = () => void;
const subscribers = new Set<Subscriber>();

const notifySubscribers = () => {
  subscribers.forEach(subscriber => subscriber());
};

export const useStore = () => {
  const [storeVersion, setStoreVersion] = useState(0);

  useEffect(() => {
    const forceUpdate = () => setStoreVersion(v => v + 1);
    subscribers.add(forceUpdate);
    return () => {
      subscribers.delete(forceUpdate);
    };
  }, []);

  const updateStore = useCallback((newStore: ChatStore) => {
    globalStore = newStore;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(globalStore));
    notifySubscribers();
  }, []);

  const createChat = useCallback((name: string): Chat => {
    const newChat: Chat = {
      id: Date.now().toString(),
      name,
      messages: [],
      timestamp: Date.now(),
      category: 'All'
    };

    updateStore({
      ...globalStore,
      chats: {
        ...globalStore.chats,
        [newChat.id]: newChat
      },
      activeChat: newChat.id
    });

    return newChat;
  }, [updateStore]);

  const addMessage = useCallback((chatId: string, text: string, sent: boolean) => {
    const chat = globalStore.chats[chatId];
    if (!chat) return;

    const newMessage = {
      id: chat.messages.length + 1,
      text,
      sent,
      timestamp: Date.now(),
      status: 'sent' as const
    };

    const updatedChat = {
      ...chat,
      messages: [...chat.messages, newMessage],
      lastMessage: text,
      timestamp: Date.now()
    };

    updateStore({
      ...globalStore,
      chats: {
        ...globalStore.chats,
        [chatId]: updatedChat
      }
    });
  }, [updateStore]);

  const setActiveChat = useCallback((chatId: string) => {
    updateStore({
      ...globalStore,
      activeChat: chatId
    });
  }, [updateStore]);

  const updateChatName = useCallback((chatId: string, newName: string) => {
    const chat = globalStore.chats[chatId];
    if (!chat) return;

    updateStore({
      ...globalStore,
      chats: {
        ...globalStore.chats,
        [chatId]: {
          ...chat,
          name: newName
        }
      }
    });
  }, [updateStore]);

  const deleteChat = useCallback((chatId: string) => {
    const { [chatId]: deletedChat, ...remainingChats } = globalStore.chats;
    const nextChat = Object.keys(remainingChats)[0] || null;

    updateStore({
      ...globalStore,
      chats: remainingChats,
      activeChat: globalStore.activeChat === chatId ? nextChat : globalStore.activeChat
    });
  }, [updateStore]);

  const updateChatCategory = useCallback((chatId: string, category: ChatCategory) => {
    const chat = globalStore.chats[chatId];
    if (!chat) return;

    updateStore({
      ...globalStore,
      chats: {
        ...globalStore.chats,
        [chatId]: {
          ...chat,
          category
        }
      }
    });
  }, [updateStore]);

  // Include storeVersion in dependencies to ensure updates
  return useMemo(() => ({
    chats: globalStore.chats,
    activeChat: globalStore.activeChat,
    createChat,
    addMessage,
    setActiveChat,
    updateChatName,
    deleteChat,
    updateChatCategory
  }), [storeVersion, createChat, addMessage, setActiveChat, updateChatName, deleteChat, updateChatCategory]); // Added storeVersion
}; 